import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL;

const STATUS_MAP = {
  published:      { bg: '#dcfce7', color: '#15803d', label: 'Published' },
  draft:          { bg: '#f3f4f6', color: '#6b7280', label: 'Draft' },
  pending_review: { bg: '#fef9c3', color: '#b45309', label: 'Pending Review' },
  rejected:       { bg: '#fee2e2', color: '#dc2626', label: 'Rejected' },
};

export default function AdminPressReleases() {
  const [prs, setPrs]             = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [search, setSearch]       = useState('');
  const [reportUrls, setReportUrls] = useState({}); // prId -> input value
  const [saving, setSaving]       = useState(null); // pr id being saved
  const { getIdToken }            = useAuth();

  useEffect(() => { fetchAll(); }, []);

  function fetchAll() {
    axios.get(`${API_URL}/api/press-releases.json`)
      .then((res) => {
        const data = res.data.pressReleases || [];
        setPrs(data);
        // Pre-fill URL inputs with existing reportFile values
        const urls = {};
        data.forEach((pr) => { if (pr.reportFile) urls[pr.id] = pr.reportFile; });
        setReportUrls(urls);
        setLoading(false);
      })
      .catch(() => { setError('Failed to load press releases.'); setLoading(false); });
  }

  async function setStatus(id, status) {
    const token = getIdToken();
    try {
      await axios.put(
        `${API_URL}/api/press-releases/set-status.json?id=${id}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPrs((prev) => prev.map((pr) => pr.id === id ? { ...pr, status } : pr));
    } catch {
      setError('Failed to update status.');
    }
  }

  async function saveReportUrl(prId) {
    const url = (reportUrls[prId] || '').trim();
    if (!url) { setError('Please enter a URL before saving.'); return; }
    setSaving(prId);
    setError('');
    const token = getIdToken();
    try {
      const res = await axios.put(
        `${API_URL}/api/press-releases/set-report-url.json?id=${prId}`,
        { url },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updated = res.data.pressRelease;
      setPrs((prev) => prev.map((pr) => pr.id === prId ? { ...pr, reportFile: updated.reportFile } : pr));
    } catch {
      setError('Failed to save report URL.');
    }
    setSaving(null);
  }

  const filtered = prs.filter((pr) =>
    !search || pr.headline?.toLowerCase().includes(search.toLowerCase()) ||
    pr.authorName?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div style={s.topBar}>
        <h2 style={s.pageTitle}>All Press Releases</h2>
        <input
          style={s.search}
          placeholder="Search by title or author..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {error && <div style={s.error}>{error}</div>}

      {loading ? (
        <div style={s.loading}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={s.empty}>No press releases found.</div>
      ) : (
        <div style={s.card}>
          {/* Header */}
          <div style={s.tableHead}>
            <span>Headline</span>
            <span>Author</span>
            <span>Status</span>
            <span>Report URL</span>
            <span>Actions</span>
          </div>

          {filtered.map((pr) => {
            const st = STATUS_MAP[pr.status] || STATUS_MAP.draft;
            return (
              <div key={pr.id} style={s.tableRow}>

                {/* Title */}
                <div>
                  <Link to={`/press-release/${pr.id}`} style={s.title}>{pr.headline}</Link>
                  <div style={s.subMeta}>{pr.category || ''} · {fmtDate(pr.createdAt)}</div>
                </div>

                {/* Author */}
                <span style={s.cell}>{pr.authorName || '—'}</span>

                {/* Status */}
                <span style={{ ...s.badge, background: st.bg, color: st.color }}>{st.label}</span>

                {/* Report URL */}
                <div style={s.reportCell}>
                  <input
                    style={s.urlInput}
                    type="url"
                    placeholder="Paste Dropbox / Drive URL..."
                    value={reportUrls[pr.id] || ''}
                    onChange={(e) => setReportUrls((prev) => ({ ...prev, [pr.id]: e.target.value }))}
                  />
                  <div style={s.reportActions}>
                    <button
                      style={s.saveBtn}
                      onClick={() => saveReportUrl(pr.id)}
                      disabled={saving === pr.id}
                    >
                      {saving === pr.id ? 'Saving...' : 'Save'}
                    </button>
                    {pr.reportFile && (
                      <a
                        href={pr.reportFile}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={s.previewLink}
                      >
                        Preview
                      </a>
                    )}
                  </div>
                </div>

                {/* Approve / Reject */}
                <div style={s.actionBtns}>
                  {pr.status !== 'published' && (
                    <button onClick={() => setStatus(pr.id, 'published')} style={s.approveBtn}>✓ Approve</button>
                  )}
                  {pr.status !== 'rejected' && (
                    <button onClick={() => setStatus(pr.id, 'rejected')} style={s.rejectBtn}>✕ Reject</button>
                  )}
                  {pr.status === 'published' && (
                    <button onClick={() => setStatus(pr.id, 'draft')} style={s.draftBtn}>Unpublish</button>
                  )}
                </div>

              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const s = {
  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', gap: '1rem' },
  pageTitle: { margin: 0, fontSize: '1.4rem', fontWeight: 700, color: '#1a1a2e' },
  search: { padding: '0.55rem 1rem', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.875rem', outline: 'none', width: 280 },
  error: { background: '#fee', border: '1px solid #fcc', padding: '0.75rem 1rem', borderRadius: 6, marginBottom: '1rem', color: '#c00', fontSize: '0.9rem' },
  loading: { textAlign: 'center', padding: '3rem', color: '#888' },
  empty: { textAlign: 'center', padding: '3rem', color: '#888', background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  card: { background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'hidden' },
  tableHead: { display: 'grid', gridTemplateColumns: '2fr 1fr 130px 220px 180px', padding: '0.7rem 1.25rem', background: '#f8f8f8', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', color: '#999', borderBottom: '1px solid #eee' },
  tableRow: { display: 'grid', gridTemplateColumns: '2fr 1fr 130px 220px 180px', padding: '1rem 1.25rem', borderBottom: '1px solid #f0f0f0', alignItems: 'center', gap: '0.5rem' },
  title: { fontWeight: 600, fontSize: '0.875rem', color: '#1a1a2e', textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  subMeta: { fontSize: '0.75rem', color: '#aaa', marginTop: 2 },
  cell: { fontSize: '0.82rem', color: '#555' },
  badge: { fontSize: '0.72rem', fontWeight: 700, padding: '0.2rem 0.55rem', borderRadius: 20, display: 'inline-block', whiteSpace: 'nowrap' },
  reportCell: { display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  urlInput: { padding: '0.3rem 0.5rem', border: '1px solid #ddd', borderRadius: 4, fontSize: '0.78rem', width: '100%', boxSizing: 'border-box' },
  reportActions: { display: 'flex', gap: '0.4rem', alignItems: 'center' },
  saveBtn: { padding: '0.25rem 0.65rem', background: '#1565c0', color: '#fff', border: 'none', borderRadius: 4, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  previewLink: { fontSize: '0.75rem', color: '#1565c0', textDecoration: 'none', fontWeight: 600 },
  actionBtns: { display: 'flex', flexDirection: 'column', gap: '0.3rem' },
  approveBtn: { padding: '0.3rem 0.65rem', background: '#dcfce7', color: '#15803d', border: 'none', borderRadius: 4, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' },
  rejectBtn: { padding: '0.3rem 0.65rem', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 4, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' },
  draftBtn: { padding: '0.3rem 0.65rem', background: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: 4, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' },
};
